import { reactive, ref, watch } from 'vue'

/**
 * UNE SECTION NE SE CONFIRME QUE COMPLÈTE.
 *
 * Retour Figma « TUNNEL 1er appel » : on découvrait les réponses manquantes à
 * la toute fin, et il fallait alors revenir, rouvrir la section, la corriger,
 * la reconfirmer. Désormais, confirmer une section où manque une réponse
 * obligatoire est REFUSÉ sur-le-champ : la section reste ouverte et ses champs
 * manquants passent en rouge, tous à la fois (cf. `confirmAttempted`).
 *
 * L'INVARIANT VAUT AUSSI POUR CE QUI EST RELU. Une analyse enregistrée avant
 * cette règle peut porter une section confirmée mais incomplète : elle est
 * décochée d'office, et `droppedNotice` le dit — une case qui saute sans un
 * mot passe pour un bug. Un devis signé, en lecture seule, n'est pas touché.
 *
 * @param {Object} deps
 * @param {Object} deps.sectionsConfirmed - État des sections (reactive).
 * @param {import('vue').Ref<Object>} deps.missingBySection - Manques par section (useBesoinMissing).
 * @param {import('vue').Ref<boolean>} deps.isQuoteSigned - Devis signé.
 * @param {Function} deps.sectionLabel - Nom d'une section, pour le message.
 */
export function useBesoinConfirmGuard({ sectionsConfirmed, missingBySection, isQuoteSigned, sectionLabel }) {
  // Sections dont une confirmation vient d'être refusée : c'est ce qui allume
  // le rouge. Avant la première tentative, rien ne s'affiche en erreur — un
  // formulaire qui s'ouvre déjà rouge accuse avant qu'on ait rien fait.
  const confirmAttempted = reactive({})
  const droppedNotice = ref(null)

  const missingIn = (key) => missingBySection.value[key] || 0

  /**
   * @param {string} key - Clé de section.
   * @returns {boolean} vrai si la section est confirmée, faux si elle est refusée.
   */
  function confirmSection(key) {
    if (missingIn(key) > 0) {
      confirmAttempted[key] = true
      return false
    }
    sectionsConfirmed[key] = true
    confirmAttempted[key] = false
    return true
  }

  /** Rouvre une section confirmée pour la modifier. */
  function reopenSection(key) {
    sectionsConfirmed[key] = false
  }

  /** Manques à signaler pour une section : zéro tant qu'on n'a pas tenté de la confirmer. */
  const refusedCount = (key) => (confirmAttempted[key] ? missingIn(key) : 0)

  // Lit les DEUX sources : une restauration qui coche une section incomplète
  // ne change pas forcément les manques, et l'inverse (questionnaires chargés
  // après la restauration) ne change pas les cases.
  watch(
    () => Object.keys(sectionsConfirmed).filter((key) => sectionsConfirmed[key] && missingIn(key) > 0),
    (incomplete) => {
      if (!incomplete.length || isQuoteSigned.value) return
      incomplete.forEach((key) => { sectionsConfirmed[key] = false })
      droppedNotice.value = noticeFor(incomplete.map(sectionLabel))
    }
  )

  return { confirmAttempted, droppedNotice, confirmSection, reopenSection, refusedCount }
}

/** Ce que dit l'écran quand une confirmation est refusée. */
export function refusedText(count) {
  return count > 1
    ? `${count} réponses obligatoires manquent dans cette section.`
    : 'Une réponse obligatoire manque dans cette section.'
}

function noticeFor(labels) {
  const names = labels.map((l) => `« ${l} »`)
  if (names.length === 1) {
    return `${names[0]} a été décochée : une réponse obligatoire y manque.`
  }
  const list = `${names.slice(0, -1).join(', ')} et ${names[names.length - 1]}`
  return `${list} ont été décochées : des réponses obligatoires y manquent.`
}
